import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, input } from '@angular/core';
import { QueryParamsHandling, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { DirectivesModule } from 'ish-core/directives.module';
import { GoogleTagManagerFacade } from 'ish-core/facades/gtm.facade';
import { ProductContextFacade } from 'ish-core/facades/product-context.facade';
import { Image } from 'ish-core/models/image/image.model';
import { ProductView } from 'ish-core/models/product-view/product-view.model';

/**
 * The Product Image Component renders the product image
 * for the given imageType and imageView or the according defaults.
 *
 * @example
 * <ish-product-image imageType="M" [link]="true"></ish-product-image>
 */

@Component({
  selector: 'ish-product-image',
  templateUrl: './product-image.component.html',
  imports: [CommonModule, RouterModule, TranslateModule, DirectivesModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductImageComponent implements OnInit {
  private translateService = inject(TranslateService);
  private context = inject(ProductContextFacade);
  private gtmFacade = inject(GoogleTagManagerFacade);
  private queryParamsHandlingInjector = inject<QueryParamsHandling>('PRODUCT_QUERY_PARAMS_HANDLING' as any, {
    optional: true,
  })!;

  /**
   * The image type (size), i.e. 'S' for the small image.
   */
  readonly imageType = input.required<string>();
  /**
   * The image view, e.g. 'front', 'back'.
   */
  readonly imageView = input<string>(undefined);

  /**
   * If true, a product link is generated around the component or the given link target is taken
   */
  readonly link = input(false);
  readonly linkTarget = input<string>(undefined);

  readonly queryParamsHandling = input<QueryParamsHandling>('');

  /**
   * A custom alt text for the img tag.
   */
  readonly altText = input<string>(undefined);
  /**
   * The image loading strategy.
   */
  readonly loading = input<'lazy' | 'eager' | 'auto'>('lazy');

  readonly lensShow = input<boolean>(false);

  readonly zoomWindowElement = input<HTMLDivElement>();

  productURL$: Observable<string>;
  productImage$: Observable<Image>;
  defaultAltText$: Observable<string>;
  visible$: Observable<boolean>;
  computedQueryParamsHandling: QueryParamsHandling;
  product: ProductView;

  ngOnInit() {
    this.visible$ = this.context.select('displayProperties', 'image');
    this.productURL$ = this.context.select('productURL');
    this.productImage$ = this.context.getProductImage$(this.imageType(), this.imageView());

    this.defaultAltText$ = combineLatest([
      this.context.select('product').pipe(
        map(product => {
          this.product = product;
          return product?.name || product?.sku || '';
        })
      ),
      this.translateService.get('product.image.text.alttext'),
      of(this.imageView() && `${this.imageView()} ${this.imageType()}`),
    ]).pipe(map(parts => parts.filter(x => !!x).join(' ')));

    this.computedQueryParamsHandling = this.queryParamsHandlingInjector ?? this.queryParamsHandling();
  }

  viewItemGTMEvent() {
    const products: ProductView[] = [this.product];
    this.gtmFacade.triggerGTMEvent('view_item', products);
  }
}
